import { motion } from 'framer-motion';
import { useLanguageStore } from '../store/languageStore';

const aidGroups = [
  {
    id: 1,
    name: {
      EN: "Food Bank Network",
      ES: "Red de Bancos de Alimentos",
      ZH: "食品银行网络",
      VI: "Mạng lưới Ngân hàng Thực phẩm",
      FIL: "Food Bank Network",
      KO: "푸드뱅크 네트워크",
      AR: "شبكة بنوك الطعام"
    },
    description: {
      EN: "Providing emergency food assistance to families in need.",
      ES: "Proporcionando asistencia alimentaria de emergencia a familias necesitadas.",
      ZH: "为有需要的家庭提供紧急食品援助。",
      VI: "Cung cấp hỗ trợ thực phẩm khẩn cấp cho các gia đình có nhu cầu.",
      FIL: "Nagbibigay ng emergency food assistance sa mga pamilyang nangangailangan.",
      KO: "필요한 가정에 긴급 식품 지원을 제공합니다.",
      AR: "توفير المساعدة الغذائية الطارئة للأسر المحتاجة."
    },
    logo: "🍲"
  },
  {
    id: 2,
    name: {
      EN: "Housing First Initiative",
      ES: "Iniciativa Vivienda Primero",
      ZH: "住房优先计划",
      VI: "Sáng kiến Nhà ở Ưu tiên",
      FIL: "Housing First Initiative",
      KO: "주거 우선 이니셔티브",
      AR: "مبادرة السكن أولاً"
    },
    description: {
      EN: "Helping individuals find stable housing solutions.",
      ES: "Ayudando a las personas a encontrar soluciones de vivienda estables.",
      ZH: "帮助个人找到稳定的住房解决方案。",
      VI: "Giúp cá nhân tìm giải pháp nhà ở ổn định.",
      FIL: "Tumutulong sa mga indibidwal na makahanap ng matatag na solusyon sa pabahay.",
      KO: "개인이 안정적인 주거 해결책을 찾도록 돕습니다.",
      AR: "مساعدة الأفراد في العثور على حلول سكنية مستقرة."
    },
    logo: "🏠"
  },
  {
    id: 3,
    name: {
      EN: "Legal Aid Society",
      ES: "Sociedad de Asistencia Legal",
      ZH: "法律援助协会",
      VI: "Hiệp hội Trợ giúp Pháp lý",
      FIL: "Legal Aid Society",
      KO: "법률 지원 협회",
      AR: "جمعية المساعدة القانونية"
    },
    description: {
      EN: "Providing free legal services to those who cannot afford them.",
      ES: "Proporcionando servicios legales gratuitos a quienes no pueden pagarlos.",
      ZH: "为无法负担的人提供免费法律服务。",
      VI: "Cung cấp dịch vụ pháp lý miễn phí cho những người không có khả năng chi trả.",
      FIL: "Nagbibigay ng libreng legal na serbisyo sa mga hindi kayang magbayad.",
      KO: "법률 서비스를 감당할 수 없는 사람들에게 무료 법률 서비스를 제공합니다.",
      AR: "توفير الخدمات القانونية المجانية لمن لا يستطيعون تحمل تكاليفها."
    },
    logo: "⚖️"
  }
];

export const AidGroupsShowcase = () => {
  const { selectedLanguage } = useLanguageStore();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section className="relative py-16 sm:py-24 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            {selectedLanguage === "EN" ? "Our Trusted Aid Partners" :
             selectedLanguage === "ES" ? "Nuestros Socios de Ayuda Confiables" :
             selectedLanguage === "ZH" ? "我们值得信赖的援助伙伴" :
             selectedLanguage === "VI" ? "Đối tác Hỗ trợ Đáng tin cậy của Chúng tôi" :
             selectedLanguage === "FIL" ? "Ang Aming Mapagkakatiwalaang Aid Partners" :
             selectedLanguage === "KO" ? "신뢰할 수 있는 지원 파트너" :
             "شركاؤنا الموثوقون في المساعدة"}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {selectedLanguage === "EN" ? "Working together to provide comprehensive support to those in need." :
             selectedLanguage === "ES" ? "Trabajando juntos para brindar apoyo integral a quienes lo necesitan." :
             selectedLanguage === "ZH" ? "共同努力为有需要的人提供全面支持。" :
             selectedLanguage === "VI" ? "Cùng nhau cung cấp hỗ trợ toàn diện cho những người có nhu cầu." :
             selectedLanguage === "FIL" ? "Magkasamang nagtatrabaho para magbigay ng komprehensibong suporta sa mga nangangailangan." :
             selectedLanguage === "KO" ? "필요한 사람들에게 포괄적인 지원을 제공하기 위해 함께 노력합니다." :
             "العمل معًا لتقديم دعم شامل لمن يحتاجون."}
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid gap-6 sm:gap-8 md:grid-cols-2 lg:grid-cols-3"
        >
          {aidGroups.map((group) => (
            <motion.div
              key={group.id}
              variants={itemVariants}
              className="group relative rounded-xl border border-border bg-card p-6 shadow-sm hover:shadow-md transition-all"
            >
              <div className="mb-4 text-4xl">{group.logo}</div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                {group.name[selectedLanguage]}
              </h3>
              <p className="text-muted-foreground">
                {group.description[selectedLanguage]}
              </p>
              <div className="absolute inset-0 rounded-xl border-2 border-primary/0 transition-colors group-hover:border-primary/20 pointer-events-none" />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}; 